import React, { useEffect, useState } from "react";
import { InfoTip } from "@/components/InfoTip";
import { BarChart3 } from "lucide-react";
import { api } from "@/lib/api";

const eur = (v) => `${Number(v || 0).toFixed(2).replace(".", ",")} €`;

const Kpi = ({ label, value, testid, hint }) => (
  <div className="p-4 border border-border" data-tip={hint}>
    <div className="sux-marker mb-1">{label}</div>
    <div className="text-2xl font-black" style={{ fontFamily: "Outfit" }} data-testid={testid}>{value}</div>
  </div>
);

// Admin: what sells, who comes back — to decide when/how to change prices.
export function AdminSales() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/sales").then(({ data }) => setD(data)).catch(() => {}); }, []);
  if (!d) return null;
  const repeatRate = d.buyers ? Math.round((100 * d.repeat_buyers) / d.buyers) : 0;
  const maxMonth = Math.max(1, ...d.by_month.map((m) => m.revenue));
  return (
    <section className="border border-border p-6 max-w-3xl mt-6" data-testid="admin-sales">
      <div className="sux-marker mb-2 flex items-center gap-2"><BarChart3 className="w-3 h-3" /> Ventes<InfoTip testid="admin-sales-info" content={"Toutes les ventes payées, par pack et par mois. Montants encaissés, avant frais Stripe et URSSAF."} /></div>
      <h3 className="text-xl font-black tracking-tight mb-4" style={{ fontFamily: "Outfit" }}>Tableau des ventes</h3>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-0">
        <Kpi label="Packs vendus" value={d.sales_count} testid="admin-sales-count" hint="Paiements réussis, hors remboursements et contestations." />
        <Kpi label="Encaissé" value={eur(d.revenue_eur)} testid="admin-sales-revenue" hint="Total payé par les clients." />
        <Kpi label="Net après Stripe" value={eur(d.net_eur)} testid="admin-sales-net" hint={`Frais Stripe estimés : ${eur(d.stripe_fees_eur)} (1,5 % + 0,25 € par paiement).`} />
        <Kpi label="30 derniers jours" value={eur(d.last_30d_eur)} testid="admin-sales-30d" hint="Encaissé sur les 30 derniers jours." />
        <Kpi label="Clients" value={d.buyers} testid="admin-sales-buyers" hint="Nombre de clients différents ayant acheté au moins une fois." />
        <Kpi label="Clients qui rachètent" value={`${d.repeat_buyers} (${repeatRate} %)`} testid="admin-sales-repeat" hint="Clients ayant acheté au moins 2 fois. Au-delà de 30 %, un abonnement facultatif devient intéressant." />
        <Kpi label="Établissements" value={`${d.org_pools} · ${d.org_credits} cr.`} testid="admin-sales-orgs" hint="Réserves établissement créées et crédits au total." />
        <Kpi label="Repris" value={d.clawed_back} testid="admin-sales-clawed" hint="Paiements remboursés ou contestés (crédits retirés)." />
      </div>
      <div className="mt-6" data-testid="admin-sales-packs">
        <div className="sux-marker mb-2">Par pack</div>
        {d.packs.length === 0 && <p className="text-sm text-muted-foreground">Aucune vente pour l'instant.</p>}
        {d.packs.map((p) => (
          <div key={p.package_id} className="flex justify-between text-sm py-2 border-b border-border">
            <span>{p.label}</span>
            <span className="font-mono">{p.count} vente(s) · {eur(p.revenue)}</span>
          </div>
        ))}
      </div>
      {d.by_month.length > 0 && (
        <div className="mt-6" data-testid="admin-sales-months">
          <div className="sux-marker mb-2">Par mois</div>
          {d.by_month.map((m) => (
            <div key={m.month} className="flex items-center gap-3 text-xs font-mono py-1">
              <span className="w-16">{m.month}</span>
              <div className="h-3 bg-[#002FA7]" style={{ width: `${(70 * m.revenue) / maxMonth}%` }} />
              <span>{eur(m.revenue)}</span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

export default AdminSales;
